/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { type FC, type ReactNode } from "react";
import { borders, theme } from "../../styles";

export interface LoadingStateProps {
  message?: string;
  icon?: ReactNode;
  minHeight?: number;
  className?: string;
  size?: "small" | "medium" | "large";
}

/**
 * Generic loading state component for full-page loading states.
 * Displays a spinner with optional message.
 */
export const LoadingState: FC<LoadingStateProps> = ({
  message,
  icon,
  minHeight = 400,
  className,
  size = "medium",
}) => {
  const spinnerSize = {
    small: "1rem",
    medium: "1.5rem",
    large: "2rem",
  }[size];

  return (
    <div
      className={className}
      role="status"
      aria-live="polite"
      aria-busy="true"
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: ${minHeight}px;
        gap: 0.5rem;
        padding: 2rem;
        text-align: center;
        color: ${theme.colors.gray500};
      `}
    >
      {icon || (
        <div
          css={css`
            width: ${spinnerSize};
            height: ${spinnerSize};
            ${borders.standard}
            border-top-color: ${theme.colors.blue500};
            border-radius: 50%;
            animation: spin 1s linear infinite;
            @keyframes spin {
              to {
                transform: rotate(360deg);
              }
            }
          `}
        />
      )}
      {message && (
        <div
          css={css`
            font-size: 0.875rem;
          `}
        >
          {message}
        </div>
      )}
    </div>
  );
};
